import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  Stack,
  TextField,
} from "@mui/material";
import ErrorMessage from "../../common/ErrorMessage/ErrorMessage";
import { ValidationResult } from "joi";
import {
  GetOrgMaintenanceTaskDetailsQuery,
  MaintenanceTaskRecurrenceFrequency,
  OrgMaintenanceTaskInput,
  OrgMaintenanceTaskRecurrenceFrequency,
} from "../../../graphql/generated";
import { isArray, isNumber, mergeWith, omit, omitBy } from "lodash";
import { orgMaintenanceTaskSchema } from "./orgMaintenanceTaskSchema";
import useConfirmBeforeLeave from "../../../utils/hooks/useConfirmBeforeLeave";
import { FormDivider } from "../../asset-management/BusinessEntityForm/BusinessEntityForm";
import { useTranslation } from "react-i18next";
import { DateTimePicker } from "@mui/x-date-pickers";
import EnumSelect from "../../common/EnumSelect";
import NumberTextField from "../../common/NumberTextField";
import i18next from "i18next";
import UserSelectContainer from "../../account/UserSelect";
import { testIds } from "./OrgMaintenanceTaskForm.testIds";

type MaintenanceTask =
  GetOrgMaintenanceTaskDetailsQuery["orgMaintenanceTaskById"];

export type OrgMaintenanceTaskFormProps = {
  initialMaintenanceTask?: MaintenanceTask;
  saving: boolean;
  onSave: (maintenanceTask: OrgMaintenanceTaskInput) => void;
};
type PartialMaintenanceTask = Partial<MaintenanceTask>;
type DeepPartialMaintenanceTask = {
  [key in keyof PartialMaintenanceTask]: Partial<PartialMaintenanceTask[key]>;
};

const OrgMaintenanceTaskForm = ({
  initialMaintenanceTask,
  saving,
  onSave,
}: OrgMaintenanceTaskFormProps) => {
  const { t } = useTranslation(["users", "common"]);
  const [validationResult, setValidationResult] =
    useState<ValidationResult<OrgMaintenanceTaskInput> | null>(null);
  const [localMaintenanceTask, setLocalMaintenanceTask] = useState<
    Partial<OrgMaintenanceTaskInput>
  >(
    initialMaintenanceTask || {
      startDate: new Date(),
    }
  );

  const getFieldError = (field: string, partialPathMatch = false) =>
    validationResult?.error?.details.find((error) =>
      partialPathMatch
        ? error.path.join(".").startsWith(field)
        : error.path.join(".") === field
    )?.message;

  const onChange = useCallback((changes: DeepPartialMaintenanceTask) => {
    setLocalMaintenanceTask((localMaintenanceTask) =>
      mergeWith({}, localMaintenanceTask, changes, (objValue, srcValue) => {
        if (isArray(srcValue)) {
          return srcValue;
        }
      })
    );
  }, []);

  const validate = () => {
    const validationResult = orgMaintenanceTaskSchema.validate(
      omit(localMaintenanceTask, ["_id", "assignedToUser"]),
      {
        abortEarly: false,
      }
    );
    setValidationResult(validationResult);
    return !validationResult.error;
  };

  useEffect(() => {
    if (validationResult) {
      validate();
    }
    // We don't want to run everytime validationResult changes
    // otherwise we ill have an infinite update loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localMaintenanceTask]);

  const { cancelConfirm } = useConfirmBeforeLeave(localMaintenanceTask);

  return (
    <Box>
      <Grid container spacing={3}>
        <Grid item sm={12}>
          <FormDivider variant="fullWidth" text="Maintenance Task Details" />
          <ErrorMessage message={validationResult?.error?.message} />
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Stack direction="row" spacing={2}>
                <TextField
                  label={t("common:label", "Label")}
                  fullWidth
                  required
                  value={localMaintenanceTask.label || ""}
                  error={!!getFieldError("label")}
                  helperText={getFieldError("label")}
                  onChange={(event) => {
                    onChange({ label: event.target.value });
                  }}
                inputProps={{ "data-testid": testIds.orgMaintenanceTaskFormLabel } as React.InputHTMLAttributes<HTMLInputElement>}
                />
              </Stack>
            </Grid>

            <Grid item xs={12} sm={4.5}>
              <DateTimePicker
                label="Start date"
                ampm={false}
                value={localMaintenanceTask?.startDate || null}
                onChange={(date) => {
                  onChange({ startDate: date });
                }}
                maxDate={localMaintenanceTask?.endDate || null}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    fullWidth
                    error={!!getFieldError("startDate")}
                    helperText={getFieldError("startDate")}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={4.5}>
              <DateTimePicker
                label="End date"
                ampm={false}
                value={localMaintenanceTask?.endDate || null}
                onChange={(date) => {
                  onChange({ endDate: date });
                }}
                minDate={localMaintenanceTask?.startDate || null}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    error={!!getFieldError("endDate")}
                    helperText={getFieldError("endDate")}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Stack direction="row" spacing={2}>
                <TextField
                  label={t("common:alertBeforeDays", "Alert before days")}
                  fullWidth
                  type="number"
                  value={
                    isNumber(localMaintenanceTask.alertBeforeDays)
                      ? String(localMaintenanceTask.alertBeforeDays) ?? ""
                      : ""
                  }
                  onChange={(event) => {
                    onChange({
                      alertBeforeDays: parseInt(event.target.value) || null,
                    });
                  }}
                inputProps={{ "data-testid": testIds.orgMaintenanceTaskFormAlertBeforeDays } as React.InputHTMLAttributes<HTMLInputElement>}
                />
              </Stack>
            </Grid>

            <Grid item xs={12}>
              <UserSelectContainer data-testid="org-maintenance-task-form-assigned-to-select"
                onChange={(usersId) => {
                  if (usersId.length) {
                    onChange({ assignedTo: usersId[usersId.length - 1] });
                    return;
                  }
                  onChange({ assignedTo: null });
                }}
                value={
                  localMaintenanceTask.assignedTo
                    ? [localMaintenanceTask.assignedTo]
                    : []
                }
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                label={t("common:recurrent", "Recurrent")}
                control={
                  <Checkbox
                    checked={!!localMaintenanceTask.recurrence}
                    onChange={(event, checked) => {
                      if (checked) {
                        onChange({
                          recurrence: {
                            frequency: MaintenanceTaskRecurrenceFrequency.Daily,
                            interval: 1,
                          },
                        });
                      } else {
                        onChange({ recurrence: null });
                      }
                    }}
                  inputProps={{ "data-testid": testIds.orgMaintenanceTaskFormEvery } as React.InputHTMLAttributes<HTMLInputElement>}
                  />
                }
              />
              {!!localMaintenanceTask.recurrence ? (
                <Stack direction="row" spacing={2}>
                  <NumberTextField
                    label="Every"
                    placeholder="1"
                    value={localMaintenanceTask.recurrence?.interval || ""}
                    onChange={(event) => {
                      onChange({
                        recurrence: {
                          interval: parseInt(event.target.value),
                        },
                      });
                    }}
                    error={!!getFieldError("recurrence.interval")}
                    helperText={getFieldError("recurrence")}
                  />

                  <EnumSelect data-testid="org-maintenance-task-form-recurrence-frequency"
                    enumObject={omitBy(
                      OrgMaintenanceTaskRecurrenceFrequency,
                      (v) => !v
                    )}
                    label={"Recurrence"}
                    fullWidth
                    withNoneOption
                    value={localMaintenanceTask.recurrence?.frequency || null}
                    error={!!getFieldError("recurrence.frequency")}
                    helperText={getFieldError("recurrence.frequency")}
                    optionLabel={orgMaintenanceTaskRecurrenceFrequencyUnitLabel}
                    onChange={(event, value) => {
                      if (!value) {
                        onChange({ recurrence: null });
                        return;
                      }
                      onChange({
                        recurrence: {
                          frequency: value,
                          interval:
                            localMaintenanceTask.recurrence?.interval || 1,
                        },
                      });
                    }}
                  />
                </Stack>
              ) : null}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row-reverse",
          pt: 3,
        }}
      >
        <Button data-testid={testIds.orgMaintenanceTaskFormSave}
          variant="contained"
          disabled={saving || !!validationResult?.error}
          size="large"
          onClick={() => {
            if (!localMaintenanceTask) {
              return;
            }

            if (validate()) {
              cancelConfirm();
              onSave(localMaintenanceTask as OrgMaintenanceTaskInput);
            }
          }}
          id="saveMaintenanceTaskButton"
        >
          {t("common:save", "Save")}
        </Button>
      </Box>
    </Box>
  );
};

export default OrgMaintenanceTaskForm;

const orgMaintenanceTaskRecurrenceFrequencyUnitLabel = (
  value: OrgMaintenanceTaskRecurrenceFrequency
): string => {
  switch (value) {
    case OrgMaintenanceTaskRecurrenceFrequency.Daily:
      return i18next.t(`common:days`, "Days");
    case OrgMaintenanceTaskRecurrenceFrequency.Weekly:
      return i18next.t(`common:weeks`, "Weeks");
    case OrgMaintenanceTaskRecurrenceFrequency.Monthly:
      return i18next.t(`common:months`, "Months");
    case OrgMaintenanceTaskRecurrenceFrequency.Yearly:
      return i18next.t(`common:years`, "Years");
    default:
      return value;
  }
};
